<script setup lang="ts">
import AccordionPanel from '@/shared/ui/Accordion/AccordionPanel.vue';

interface Props {
  isCollapsed?: boolean;
  isClosable?: boolean;
  isVisiblePanel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isCollapsed: true,
  isClosable: false,
  isVisiblePanel: true,
});

const emit = defineEmits(['toggle', 'close', 'open']);

const collapse: Ref<null | boolean> = ref(null);
const isLocalCollapsed = ref(props.isCollapsed);

const close = (isToggled = false): void => {
  isLocalCollapsed.value = true;
  emit(isToggled ? 'toggle' : 'close', isLocalCollapsed.value);
};

const open = (isToggled = false): void => {
  isLocalCollapsed.value = false;
  emit(isToggled ? 'toggle' : 'open', isLocalCollapsed.value);
};

const toggleHandler = (): void => {
  if (isLocalCollapsed.value) {
    open(true);
  } else {
    close(true);
  }
};

watch(
  () => props.isClosable,
  val => {
    if (val) {
      close();
    }
  },
  { immediate: false },
);

watch(
  () => props.isCollapsed,
  val => {
    if (val) {
      close();
    } else {
      open();
    }
  },
  { immediate: false },
);
</script>

<template>
  <div :class="['accordion', { _active: !isLocalCollapsed }]">
    <accordion-panel
      v-if="isVisiblePanel"
      :collapsed="isLocalCollapsed"
      @toggle="toggleHandler"
    >
      <slot name="accordion" />
      <template #icon>
        <slot name="icon" />
      </template>
    </accordion-panel>
    <slot v-else name="accordion" />
    <div ref="collapse" class="accordion__wrapper">
      <slot />
    </div>
  </div>
</template>

<style lang="less">
.accordion {
  .flex-column(5px);

  width: 100%;

  &__wrapper {
    width: 100%;
    max-height: 0;
    overflow: hidden;
    background-color: @color-gallery;
    border-radius: @size-x3;
    opacity: 0;
    transition:
      opacity @A ease-in-out,
      max-height @A ease-in-out;

    @media @1024 {
      margin-top: 2px;
    }
  }

  &._active &__wrapper {
    opacity: 1;
    max-height: 900px;
  }
}
</style>
